/**
 * useFocusOnMount — DESIGN_CONTRACT §7 Awareness Contract.
 *
 * Reads `?focus=<id>` from the URL on mount. When the matching list row
 * appears in the DOM (poll for up to 4 seconds while React renders + data
 * fetches resolve), smoothly scrolls it into view and adds the
 * `.hv-focus-pulse` cyan-ring animation for 2.4 s. Then strips the param
 * from history so a refresh doesn't re-trigger.
 *
 * Convention: every list row component MUST render
 *   <div data-focus-id={item.id} ...>
 * for the hook to find it.
 *
 * Usage:
 *   useFocusOnMount({ ready: !loading, deps: [items] });
 *
 * `ready` should flip to true once the list has data (otherwise the poll
 * burns budget waiting for an empty page). `deps` controls when the hook
 * re-attempts the lookup (e.g. when the items array updates after a
 * pagination/filter change).
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const POLL_INTERVAL_MS = 120;
const MAX_WAIT_MS = 4000;
const PULSE_CLASS = "hv-focus-pulse";
const PULSE_DURATION_MS = 2400;

export default function useFocusOnMount({ ready = true, deps = [] } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (!ready) return;
    const params = new URLSearchParams(location.search);
    const focusId = params.get("focus");
    if (!focusId) return;
    if (triggeredRef.current) return;

    let cancelled = false;
    const start = Date.now();

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-focus-id="${CSS.escape(focusId)}"]`);
      if (el) {
        triggeredRef.current = true;
        try {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch {
          el.scrollIntoView();
        }
        el.classList.add(PULSE_CLASS);
        setTimeout(() => {
          el.classList.remove(PULSE_CLASS);
        }, PULSE_DURATION_MS);
        // Strip the focus param so refresh / back-nav don't re-fire.
        params.delete("focus");
        const qs = params.toString();
        navigate(
          { pathname: location.pathname, search: qs ? `?${qs}` : "", hash: location.hash },
          { replace: true },
        );
        return;
      }
      if (Date.now() - start < MAX_WAIT_MS) {
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    tick();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, location.search, ...deps]);
}
